import { openFDCreditFAQModal } from "@faire/deprecated-retailer-visitor-shared/components/App/AppModals/utils";
import { OpenFaireDirectFirstOrderIncentiveModalButton } from "@faire/deprecated-retailer-visitor-shared/components/Modals/FaireDirectModals/FaireDirectFirstOrderIncentiveModal/OpenFaireDirectFirstOrderIncentiveModalButton";
import { useFaireDirectFirstOrderIncentive } from "@faire/deprecated-retailer-visitor-shared/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import { useSettingEnableFDRASitewideBannerUpdates } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingEnableFDRASitewideBannerUpdates";
import { useIsPreOrderExcludedForBrandPromo } from "@faire/deprecated-retailer-visitor-shared/stores/domain/Events/FaireMarkets/useIsPreorderExcludedForBrandPromo";
import { MarketPromoLine } from "@faire/deprecated-retailer-visitor-shared/views/product/ProductDetails/__internal__/Header/MarketPromoLine";
import { SimpleProductPromo } from "@faire/deprecated-retailer-visitor-shared/views/product/ProductDetails/__internal__/Header/SimpleProductPromotions";
import { IProductDetailsHeaderProps } from "@faire/deprecated-retailer-visitor-shared/views/product/ProductDetails/Header";
import { ProductDetails } from "@faire/deprecated-retailer-visitor-shared/views/product/ProductDetails/model";
import { Decorative } from "@faire/design-tokens";
import { IInformationMessage } from "@faire/web-api--source/indigofair/data/IInformationMessage";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import { useTraceComponentRender } from "@faire/web/common/tracing/useTraceComponentRender";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { EmbeddedLink } from "@faire/web/ui/EmbeddedLink";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { History } from "history";
import React, { type JSX } from "react";
import { useHistory } from "react-router-dom";

import { useProductPromotionsState } from "../../AddToCart/useProductPromotionsState";

export const ProductDetailsPromotion: React.FC<
  Pick<IProductDetailsHeaderProps, "brandPromotion" | "brand"> & {
    product: ProductDetails.IProduct;
  }
> = ({ brandPromotion, brand, product }) => {
  useTraceComponentRender("ProductDetailsPromotion");
  const { hasIncentiveAndBrandTokenMatches } =
    useFaireDirectFirstOrderIncentive();

  const productPromotionData = useProductPromotionsState(product?.token ?? "");

  const isPreOrderExcludedForBrandPromo = useIsPreOrderExcludedForBrandPromo();

  if (hasIncentiveAndBrandTokenMatches(brand?.token ?? "")) {
    return (
      <>
        <Spacer height="1x" />
        <Row>
          <Typography
            variant="paragraphSansRegular"
            color={Decorative.shade.orange}
          >
            <FaireDirectProductPromoText brand={brand} />
          </Typography>
        </Row>
      </>
    );
  }

  const isPreOrderProductAndExcludedFromMarketPromo =
    product?.preorderable &&
    isPreOrderExcludedForBrandPromo(
      brandPromotion?.promotional_event_type,
      brandPromotion?.ship_availability
    );

  if (productPromotionData?.marketPromo?.text) {
    return !isPreOrderProductAndExcludedFromMarketPromo ? (
      <>
        <Spacer height="2x" />
        <MarketPromoLine promotion={productPromotionData.marketPromo.text} />
      </>
    ) : null;
  }

  return (
    <SimpleProductPromo
      promotions={productPromotionData?.promotionStyleBanners}
      position={IInformationMessage.Position.DEFAULT}
    />
  );
};

const OpenFDCreditFAQModalLink = (
  brand: IProductPageResponse.IPdpBrand | undefined,
  history: History,
  brandName: string
) => {
  return function (chunk: JSX.Element) {
    return (
      <EmbeddedLink
        style={{
          color: Decorative.shade.orange,
          textDecorationColor: Decorative.shade.orange,
        }}
        onClick={() =>
          openFDCreditFAQModal(
            brand?.name ?? brandName,
            history,
            brand?.minimum_order_amount
          )
        }
      >
        {chunk}
      </EmbeddedLink>
    );
  };
};

const FaireDirectProductPromoText: React.FC<{
  brand: IProductPageResponse.IPdpBrand | undefined;
}> = ({ brand }) => {
  const history = useHistory();
  const {
    brandName,
    isFaireDirectCreditPercentageBased,
    formattedPercentageBasedNumber,
    formattedFixedBasedAmount,
    formattedMaxPercentageDiscountAmount,
  } = useFaireDirectFirstOrderIncentive();
  const enableFDRASitewideBannerUpdates =
    useSettingEnableFDRASitewideBannerUpdates();
  if (isFaireDirectCreditPercentageBased) {
    if (enableFDRASitewideBannerUpdates) {
      return (
        <StrictLocalMsg
          id="KuTnls"
          defaultMessage="{faireDirectPercentageDiscountNumber} off (up to {maximumDiscountMoney}) & 1 year of free shipping. <LinkFormatting>Learn more</LinkFormatting>"
          description={{
            text: "discount promotion text that is shown above a single product",
            img: "https://cdn.faire.com/fastly/5d361f660142a25f9b31fd38aa4b86bdaca42c068e1ec55810bb853dfa6fecb5.png",
          }}
          values={{
            faireDirectPercentageDiscountNumber: formattedPercentageBasedNumber,
            maximumDiscountMoney: formattedMaxPercentageDiscountAmount,
            LinkFormatting: OpenFaireDirectFirstOrderIncentiveModalButton,
          }}
        />
      );
    }
    return (
      <StrictLocalMsg
        id="507W/0"
        defaultMessage="{faireDirectPercentageDiscountNumber} off & 1 year of free shipping. <LinkFormatting>Learn more</LinkFormatting>"
        description={{
          text: "discount promotion text that is shown above a single product",
          img: "https://cdn.faire.com/fastly/92fe0b60e4478eaa42d0d49b3e82bad7ef1c39e50a827fbe12c7e9babaf2ca53.png",
        }}
        values={{
          faireDirectPercentageDiscountNumber: formattedPercentageBasedNumber,
          LinkFormatting: OpenFDCreditFAQModalLink(
            brand,
            history,
            brandName ?? ""
          ),
        }}
      />
    );
  }
  return (
    <StrictLocalMsg
      id="mqJc54"
      defaultMessage="{incentiveMoney} off & 1 year of free shipping. <LinkFormatting>Learn more</LinkFormatting>"
      description={{
        text: "discount promotion text that is shown above a single product",
        img: "https://cdn.faire.com/fastly/92fe0b60e4478eaa42d0d49b3e82bad7ef1c39e50a827fbe12c7e9babaf2ca53.png",
      }}
      values={{
        incentiveMoney: formattedFixedBasedAmount,
        LinkFormatting: OpenFDCreditFAQModalLink(
          brand,
          history,
          brandName ?? ""
        ),
      }}
    />
  );
};
